/**
 * The single-session guard's detection half, and the grace period. Mirrored
 * from `bimgo-ai-web/client/src/lib/authSession.ts` (their PR #167), adapted to
 * 2.5's lazily-created client (`supabase()` is a function here, not a module
 * const). #75.
 *
 * Detects that this device lost its session to Supabase's "single session per
 * user" rule (Auth → Sessions). When that setting is on, signing in on a second
 * device does **not** delete this device's row in `auth.sessions` — GoTrue
 * leaves it in place and only judges it when the refresh token is presented
 * (see `probeSession`). So the access token keeps working, api-v2's `get_user()`
 * check keeps answering 200, and supabase-js goes on believing it is signed in
 * for up to an hour. That is both why this file has to ask GoTrue explicitly,
 * and what makes the grace period possible at all.
 *
 * Split so each half only does what it can actually know:
 *   - `authFetch` reads GoTrue's error code off the wire and records *why* the
 *     session died — the only place positioned to see it before supabase-js acts.
 *   - `checkForRevokedSession` triggers a check and guarantees local teardown; it
 *     never decides what the user is told.
 *   - `decideOnAnnouncement` is the winner/loser rule, pure, so the one decision
 *     that puts a banner on the right device can be tested without Realtime.
 */
import { isAuthApiError } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import { authLog } from "./authDebug";

/** sessionStorage key holding why the user landed back on login. Per-tab: only
 *  the tab that was actually signed out should explain itself. */
export const SIGNED_OUT_REASON_KEY = "signed_out_reason";

/** The account was signed in somewhere else and this device lost. Written only
 *  when a peer announcement proved it — see `noteIfSessionRevoked`. */
export const SIGNED_OUT_ANOTHER_DEVICE = "another_device";

/** The session is gone and nothing says why. GoTrue's codes cannot distinguish
 *  "superseded by a newer login" from "rotated or expired", so this is what an
 *  unexplained revocation gets (#124). */
export const SIGNED_OUT_SESSION_ENDED = "session_ended";

/** GoTrue codes that mean "the session this request was made with is gone".
 *
 *  `refresh_token_not_found` is what this project actually answers with when a
 *  newer login has taken the session over — confirmed from the wire by
 *  bimgo-ai-web, and the one that matters, since the refresh endpoint is what
 *  the token is presented to. It was missing here, so the losing device's own
 *  probe fell through to "unknown" for the very case it exists to catch.
 *  `session_not_found` comes back from `GET /auth/v1/user` once the row has been
 *  deleted; `session_expired` is the documented refresh rejection ("Revoked by
 *  Newer Login"). Both are kept because GoTrue picks between them by version.
 *
 *  These say the session is **gone**, not *why*. Every one of them has a cause
 *  with no second device involved: a rotated token whose replacement was never
 *  persisted, two refreshes racing into reuse detection, Supabase's session
 *  time-box or inactivity timeout, or server-side cleanup of the row. Treating
 *  the whole list as "signed in elsewhere" is what made the login page assert a
 *  second device on an ordinary expiry (#124), so the wording now follows the
 *  peer announcement instead — see `noteIfSessionRevoked`. */
const REVOKED_SESSION_CODES = ["refresh_token_not_found", "session_not_found", "session_expired"];

/**
 * How long this device keeps working after the account is used elsewhere.
 *
 * Possible only because GoTrue leaves the losing session's row in place — the
 * access token stays good, so api-v2 keeps answering 200 through the window.
 *
 * Two things can still cut it short, both accepted:
 *   - supabase-js refreshes on its own once the token is within 90s of expiring,
 *     and that refresh is rejected. `stopAutoRefresh()` would not hold:
 *     GoTrueClient restarts the ticker on every hidden → visible transition.
 *     With a one-hour JWT this costs the grace only when the other sign-in lands
 *     in the last ~2 minutes of a token's life.
 *   - A device that never received the announcement (socket dead throughout) is
 *     signed out on its next focus check with no window. Under "a fixed window
 *     from the other login" that is usually right — the deadline has long passed
 *     — but harsh when the login was in fact recent.
 *
 * Same number as the web client. A Revit user mid-operation may want longer;
 * it is one constant.
 */
export const SIGNED_OUT_GRACE_MS = 5 * 60_000;

/** localStorage key holding the pending sign-out deadline. */
const GRACE_KEY = "session_signout_grace";

export interface SessionGrace {
  /** The session this deadline belongs to, so a later sign-in ignores it. */
  sessionId: string;
  /** Epoch ms at which this device gets signed out. */
  deadlineAt: number;
}

/**
 * Deliberately in `localStorage`, not `sessionStorage`: a reload must resume
 * the *same* deadline. Per-tab storage would restart the clock on every
 * refresh, which turns a bounded grace into an unlimited one. The desktop is one
 * window, but "reload" is a menu item and a crash-recovery path, and both must
 * find the deadline where they left it.
 */
let cachedGrace: SessionGrace | null | undefined;
const graceListeners = new Set<(grace: SessionGrace | null) => void>();

function readStoredGrace(): SessionGrace | null {
  try {
    const raw = localStorage.getItem(GRACE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const record = parsed as { sessionId?: unknown; deadlineAt?: unknown } | null;
    if (typeof record?.sessionId !== "string" || typeof record?.deadlineAt !== "number") {
      return null;
    }
    return { sessionId: record.sessionId, deadlineAt: record.deadlineAt };
  } catch {
    // Unavailable or corrupt storage means no grace, never a crash.
    return null;
  }
}

function writeGrace(grace: SessionGrace | null): void {
  cachedGrace = grace;
  try {
    if (grace) localStorage.setItem(GRACE_KEY, JSON.stringify(grace));
    else localStorage.removeItem(GRACE_KEY);
  } catch {
    // The in-memory copy still drives this window; only across-reload
    // continuity is lost.
  }
  graceListeners.forEach((listener) => listener(grace));
}

/** The pending sign-out deadline, or null when this device has not lost. */
export function getGrace(): SessionGrace | null {
  if (cachedGrace === undefined) cachedGrace = readStoredGrace();
  return cachedGrace;
}

/**
 * Begins the countdown. A grace already running for this session is left
 * alone — devices announce themselves on every reconnect, and letting a repeat
 * announcement push the deadline back would make the window unbounded.
 */
export function startGrace(sessionId: string, deadlineAt: number): void {
  const existing = getGrace();
  if (existing && existing.sessionId === sessionId) return;
  writeGrace({ sessionId, deadlineAt });
}

export function clearGrace(): void {
  if (getGrace() === null) return;
  writeGrace(null);
}

/** Discards a deadline stored for some other session, e.g. after a new login. */
export function dropForeignGrace(sessionId: string): void {
  const existing = getGrace();
  if (existing && existing.sessionId !== sessionId) writeGrace(null);
}

export function subscribeGrace(listener: (grace: SessionGrace | null) => void): () => void {
  graceListeners.add(listener);
  return () => {
    graceListeners.delete(listener);
  };
}

/** What a check learned. "unknown" covers both a skipped and an inconclusive check. */
export type ProbeResult = "valid" | "revoked" | "unknown";

/** The `session_id` and login time carried by a Supabase access token. */
export interface SessionClaims {
  sessionId: string | null;
  /** Epoch *seconds* of the login, from the `amr` claim. */
  loginAt: number | null;
}

/**
 * Reads the two claims the session guard needs out of an access token.
 *
 * `amr` ("authentication methods reference") carries the timestamp of the login
 * itself and — unlike `iat` — survives token refreshes unchanged, which is what
 * makes it usable as a stable "whose session is newer" comparison.
 *
 * Hand-rolled rather than using supabase-js's `decodeJWT`, which is not part of
 * its public API. Nothing here is trusted for authorisation — it only decides
 * whether to show a banner and when to ask GoTrue.
 */
export function readSessionClaims(accessToken: string | null | undefined): SessionClaims {
  const empty: SessionClaims = { sessionId: null, loginAt: null };
  if (!accessToken) return empty;

  try {
    const segment = accessToken.split(".")[1];
    if (!segment) return empty;

    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    // Via TextDecoder rather than atob alone: the payload is UTF-8 and can carry
    // non-ASCII (a display name, say), which atob's binary string would mangle.
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as {
      session_id?: unknown;
      amr?: unknown;
    };

    const methods = Array.isArray(claims.amr) ? (claims.amr as { timestamp?: unknown }[]) : [];
    const timestamps = methods
      .map((method) => method?.timestamp)
      .filter((value): value is number => typeof value === "number");

    return {
      sessionId: typeof claims.session_id === "string" ? claims.session_id : null,
      // Earliest method: re-authenticating with a second factor appends to
      // `amr`, and the session still began at the first one.
      loginAt: timestamps.length > 0 ? Math.min(...timestamps) : null,
    };
  } catch {
    return empty;
  }
}

/** What one device tells the others when it connects, as received. */
export interface PeerAnnouncement {
  sessionId: string | null;
  /** Epoch seconds. */
  loginAt: number | null;
}

export type AnnouncementDecision =
  /** Our own session announcing — a reconnect of this very device. */
  | { kind: "ignore"; reason: "own-session" }
  /** An older session announcing on reconnect — the device *we* displaced. */
  | { kind: "ignore"; reason: "older-session" }
  /** Not enough to compare; fall back to asking GoTrue at once. */
  | { kind: "probe" }
  /** A newer session took over: keep working until `deadlineAt`, then confirm. */
  | { kind: "grace"; deadlineAt: number };

/**
 * The winner/loser rule — the whole reason the announcement carries a payload.
 *
 * Devices announce on every *reconnect*, not only on a fresh sign-in, so the
 * loser announces too. Without an age comparison the winner would start a
 * countdown against the device it had just displaced. Two rules:
 *
 *   - Ignore our own `sessionId`. (In the browser client that is sibling tabs;
 *     here it is this window reconnecting.)
 *   - Ignore an announcement whose `loginAt` is not newer than ours.
 *
 * Missing either field, degrade to the old immediate check instead of
 * guessing: the loser is signed out at once — exactly as before the grace
 * existed — and the winner's check simply comes back valid. Starting a grace
 * on a guess would put the banner on whichever device happened to reconnect,
 * winner included.
 *
 * The deadline is anchored to the **winner's** login rather than to now, so a
 * device that hears about it late is not granted a fresh window.
 */
export function decideOnAnnouncement(
  mine: SessionClaims,
  peer: PeerAnnouncement,
  graceMs: number = SIGNED_OUT_GRACE_MS,
): AnnouncementDecision {
  if (peer.sessionId && mine.sessionId && peer.sessionId === mine.sessionId) {
    return { kind: "ignore", reason: "own-session" };
  }
  if (peer.loginAt === null || mine.loginAt === null || !mine.sessionId) {
    return { kind: "probe" };
  }
  if (peer.loginAt <= mine.loginAt) {
    return { kind: "ignore", reason: "older-session" };
  }
  return { kind: "grace", deadlineAt: peer.loginAt * 1000 + graceMs };
}

let inFlight: Promise<ProbeResult> | null = null;
let lastCheckedAt = 0;

/** Floor between checks. Each spends a token refresh, and GoTrue rate-limits
 *  those to 1800/hour per IP — which a whole office shares behind one NAT. */
const MIN_CHECK_INTERVAL_MS = 5_000;

/**
 * Ask GoTrue whether this device is still the session that counts, and sign out
 * locally if it isn't. Safe to call as often as you like — concurrent calls
 * share one check, bursts are throttled, and no stored session is a no-op.
 *
 * Pass `force` only from the grace deadline. Everything else must go through
 * the suppression below.
 */
export function checkForRevokedSession(options: { force?: boolean } = {}): Promise<ProbeResult> {
  if (inFlight) return inFlight;

  // The single most important line in the grace period. Checking is not a
  // read-only act: `probeSession` presents the refresh token, and supabase-js
  // tears the stored session down the moment that is rejected — before this
  // module ever sees the error. So a device serving out its grace must not probe
  // at all, or the window collapses to nothing.
  //
  // Living here rather than at each trigger covers every path at once: the peer
  // announcement, the focus and visibility checks in `useSessionGuard`, and the
  // 401/403 hook in `apiFetch`.
  const grace = getGrace();
  if (grace && !options.force && Date.now() < grace.deadlineAt) {
    authLog("skipped, grace period runs for another", grace.deadlineAt - Date.now(), "ms");
    return Promise.resolve("unknown");
  }

  const sinceLast = Date.now() - lastCheckedAt;
  if (!options.force && sinceLast < MIN_CHECK_INTERVAL_MS) {
    authLog(`skipped, checked ${sinceLast}ms ago`);
    return Promise.resolve("unknown");
  }

  inFlight = probeSession().finally(() => {
    inFlight = null;
    lastCheckedAt = Date.now();
  });
  return inFlight;
}

/**
 * Forces a token refresh, the only call that can answer the question. "Single
 * session per user" does not delete the losing row; GoTrue judges it when the
 * refresh token is presented, so `refreshSession()` triggers the enforcement on
 * demand instead of waiting up to an hour. `getUser()` is no use — the row still
 * exists and answers 200 for a device that has already lost.
 *
 * On rejection supabase-js treats it as non-retryable, drops the stored session
 * and emits SIGNED_OUT by itself; `authFetch` has already recorded the reason
 * from the same response.
 */
async function probeSession(): Promise<ProbeResult> {
  const { error } = await supabase().auth.refreshSession();

  if (!error) {
    authLog("refresh accepted — this device still holds the current session");
    return "valid";
  }

  authLog("refresh rejected:", {
    name: error.name,
    status: error.status,
    code: (error as { code?: string }).code,
  });

  // Only a definitive answer from the auth API counts. A dropped connection, a
  // CORS failure or a 5xx arrives as AuthRetryableFetchError and means "we don't
  // know" — never sign someone out over a network blip. AuthSessionMissingError
  // (nothing stored) is filtered by the same guard.
  if (!isAuthApiError(error)) return "unknown";

  // A named revoked-session code is definitive on its own, whatever status it
  // arrives with: `refresh_token_not_found` comes back as **400**, so testing
  // the status alone reports "inconclusive" for the very case this exists to
  // catch. The status check stays as the fallback for a rejection GoTrue does
  // not name.
  const code = (error as { code?: string }).code;
  const revokedByCode = typeof code === "string" && REVOKED_SESSION_CODES.includes(code);
  if (!revokedByCode && error.status !== 401 && error.status !== 403) return "unknown";

  // "local", not the default "global": this session is finished server-side, and
  // a global sign-out would take down the device that just won.
  await supabase().auth.signOut({ scope: "local" });

  // The deadline has done its job. Leaving it behind would suppress the *next*
  // session's checks until something noticed the session id had changed.
  clearGrace();
  return "revoked";
}

/**
 * `fetch` for the Supabase client, wrapped so a lost session can be explained
 * instead of silently bouncing the user to login. This is the only place that
 * sees GoTrue's error body before supabase-js acts on it, and it only runs when
 * a request was actually made — so a session existed to make it with.
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);

  // Awaited on purpose: the login page reads the reason when the redirect that
  // follows the sign-out mounts it.
  if (!response.ok && isRevocableAuthRequest(input)) {
    await noteIfSessionRevoked(response);
  }

  return response;
}

/**
 * A GoTrue call that presents this device's token and can therefore come back
 * "your session is gone". `/auth/v1/logout` is excluded: it answers 403
 * `session_not_found` for an already-ended session, and a user who pressed
 * "Sign out" themselves must never be told another device kicked them off. The
 * `/auth/v1/` prefix also keeps us off realtime-js's own HTTP endpoints, which
 * share this fetch.
 */
function isRevocableAuthRequest(input: RequestInfo | URL): boolean {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  return url.includes("/auth/v1/") && !url.includes("/auth/v1/logout");
}

/**
 * The code out of a GoTrue error body. Exported for the test, because the
 * shape is the whole bug: GoTrue puts the machine-readable code in `error_code`
 * and uses `code` for the HTTP status **as a number**:
 *
 *   {"code":403,"error_code":"session_expired","msg":"Invalid Refresh Token:
 *    Session Expired (Revoked by Newer Login)"}
 *
 * Reading `code` alone compared 403 against a list of strings and never matched,
 * so the sign-out was never explained — the login page's notice has been
 * unreachable since it was written. `code` is still read as the fallback for
 * the older error shape, where it did carry the string.
 */
export function revocationCodeOf(body: unknown): string | null {
  const record = body as { error_code?: unknown; code?: unknown } | null;
  const candidate = record?.error_code ?? record?.code;
  return typeof candidate === "string" && REVOKED_SESSION_CODES.includes(candidate)
    ? candidate
    : null;
}

async function noteIfSessionRevoked(response: Response): Promise<void> {
  try {
    // clone: supabase-js still has to read this body itself.
    const body: unknown = await response.clone().json();

    // Anything else — a wrong password, a rate limit, an expired invite — is
    // somebody else's problem and must not leave a "signed in elsewhere" notice
    // behind for the next visit to the login page.
    const code = revocationCodeOf(body);
    if (!code) {
      authLog("auth error ignored, not a revoked session");
      return;
    }

    /**
     * Which explanation the login page may give.
     *
     * A running grace is the **only** positive evidence that another device took
     * the account over: a peer announced itself with a newer `loginAt` and this
     * device accepted the comparison (`decideOnAnnouncement`). The GoTrue code
     * carries no such information — see `REVOKED_SESSION_CODES` — so without the
     * announcement the honest answer is that the session ended, not that someone
     * else signed in (#124).
     *
     * The grace is still readable here: `authFetch` awaits this inside the failing
     * request, which is strictly before `probeSession` signs out and clears it,
     * and before supabase-js emits the SIGNED_OUT that clears it in AuthProvider.
     *
     * A device that was closed while another signed in therefore gets the neutral
     * wording. That is the deliberate trade: it never saw the announcement, and
     * its rejection is indistinguishable from an expired token.
     */
    const reason = getGrace() ? SIGNED_OUT_ANOTHER_DEVICE : SIGNED_OUT_SESSION_ENDED;
    authLog("session revoked, recording the reason:", code, "->", reason);
    sessionStorage.setItem(SIGNED_OUT_REASON_KEY, reason);
  } catch {
    // A non-JSON body or unavailable sessionStorage costs the notice, never the
    // request — the sign-out itself is supabase-js's job and still happens.
  }
}
